import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, Clock, ExternalLink, Scissors, Sparkles, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatAiredDay, formatDuration } from '@/lib/format';
import { OriginalVideoDownloadButton } from '@/components/editor/OriginalVideoDownloadButton';
import ProFeatureDialog from '@/components/ProFeatureDialog';
import { resolveOriginalVideoDownloadUrl } from '@/lib/downloads/resolveOriginalVideoDownloadUrl';
import { useHasFeature } from '@/lib/useHasFeature';

export interface CampaignVideoCardChannel {
  channel_id: string;
  name: string;
  thumbnail?: string | null;
}

export interface CampaignVideoCardLibrary {
  id: string;
  name: string;
  thumbnail_url?: string | null;
  duration_seconds?: number | null;
  /** When the video aired (a live's start time), ISO. */
  aired_at?: string | null;
  base_video_url?: string | null;
  original_video_url?: string | null;
  cut_video_url?: string | null;
  compressed_base_video_url?: string | null;
  format?: string | null;
  language?: string | null;
  start_time?: number | null;
  end_time?: number | null;
  youtube_video_id?: string | null;
  /** Source youtube_download documentId; what campaign exclusions point at. */
  youtube_download_id?: string | null;
  youtube_channels?: CampaignVideoCardChannel[] | null;
  active_cpm_campaigns?: Array<{ id: string }> | null;
}

/**
 * Shape passed through `location.state.preloadedLibrary` so the destination
 * (CommunityLibraryPage) can open the copy dialog without waiting for its
 * own query. Keep in sync with CopyLibraryDialog's `library` prop.
 */
export type PreloadedCampaignLibrary = Pick<
  CampaignVideoCardLibrary,
  'id' | 'name' | 'base_video_url' | 'duration_seconds' | 'format' | 'language' | 'start_time' | 'end_time' | 'active_cpm_campaigns'
>;

interface CampaignVideoCardProps {
  library: CampaignVideoCardLibrary;
  /** channel_ids targeted by the campaign — used to pick the best link target. */
  campaignChannelIds: string[];
  /** When set, opens the clip-creation dialog in place instead of leaving the page. */
  onGenerateClips?: (library: CampaignVideoCardLibrary) => void;
  quotaExempt?: boolean;
  /** Admins only: asks to remove this video from the campaign. */
  onRemoveFromCampaign?: (library: CampaignVideoCardLibrary) => void;
}

/**
 * Pick the channel that the campaign targets when the library has many.
 * Falls back to the first channel.
 */
function pickChannel(
  channels: CampaignVideoCardChannel[],
  campaignChannelIds: string[],
): CampaignVideoCardChannel | null {
  if (channels.length === 0) return null;
  const targeted = new Set(campaignChannelIds);
  return channels.find((c) => targeted.has(c.channel_id)) || channels[0];
}

const CampaignVideoCard = ({
  library, campaignChannelIds, onGenerateClips, quotaExempt = true, onRemoveFromCampaign,
}: CampaignVideoCardProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const hasProAccess = useHasFeature('pro_features');
  const [proDownloadOpen, setProDownloadOpen] = useState(false);
  const channels = library.youtube_channels || [];
  const channel = pickChannel(channels, campaignChannelIds);
  const duration = library.duration_seconds && library.duration_seconds > 0
    ? formatDuration(library.duration_seconds)
    : null;
  const airedDay = formatAiredDay(library.aired_at, i18n?.language ?? 'pt');
  const originalDownloadUrl = resolveOriginalVideoDownloadUrl({
    originalVideoUrl: library.original_video_url,
    cutVideoUrl: library.cut_video_url,
    baseVideoUrl: library.base_video_url,
    compressedBaseVideoUrl: library.compressed_base_video_url,
  });

  const params = new URLSearchParams();
  if (channel?.channel_id) params.set('channelId', channel.channel_id);
  params.set('libraryId', library.id);
  const href = `/community-library?${params.toString()}`;

  const handleGenerateClips = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onGenerateClips) {
      onGenerateClips(library);
      return;
    }
    navigate(href, { state: { preloadedLibrary: library } });
  };

  return (
    <>
      <Card
        data-library-id={library.id}
        className="overflow-hidden p-0 border-border/50 flex flex-col group"
      >
        <button
          className="block relative aspect-video bg-gradient-secondary overflow-hidden w-full text-left cursor-pointer"
          onClick={handleGenerateClips}
        >
          {library.thumbnail_url ? (
            <img
              src={library.thumbnail_url}
              alt={library.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <span className="text-muted-foreground text-xs px-2 text-center">{library.name}</span>
            </div>
          )}
          {duration && (
            <div className="absolute top-2 right-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 text-white text-xs font-medium backdrop-blur-sm">
                <Clock className="w-3 h-3" />
                {duration}
              </span>
            </div>
          )}
          <div className="absolute bottom-2 left-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/80 text-primary-foreground text-[10px] font-semibold backdrop-blur-sm">
              <Sparkles className="w-2.5 h-2.5" />
              {t('campaign.curatedRecommendation')}
            </span>
          </div>
        </button>

        <div className="p-3 space-y-2 flex flex-col flex-1">
          <button className="text-left" onClick={handleGenerateClips}>
            <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors duration-200">
              {library.name}
            </h3>
          </button>
          {(channel || airedDay) && (
            <div className="flex items-center gap-2 min-w-0">
              {channel && (channel.thumbnail ? (
                <img
                  src={channel.thumbnail}
                  alt={channel.name}
                  className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {channel.name.charAt(0).toUpperCase()}
                </div>
              ))}
              {channel && <span className="text-xs text-muted-foreground truncate">{channel.name}</span>}
              {channel && airedDay && <span className="text-xs text-muted-foreground" aria-hidden="true">·</span>}
              {airedDay && (
                <time dateTime={library.aired_at ?? undefined} className="text-xs text-muted-foreground flex-shrink-0">
                  {airedDay}
                </time>
              )}
            </div>
          )}
          <div className="flex gap-2 mt-auto">
            <Button
              variant="gradient"
              size="sm"
              className="flex-1 gap-1.5 text-xs h-8"
              onClick={handleGenerateClips}
            >
              <Scissors className="w-3 h-3" />
              {t('campaign.generateAiClips')}
            </Button>
            {originalDownloadUrl && (
              <OriginalVideoDownloadButton
                sourceUrl={originalDownloadUrl}
                hasProAccess={hasProAccess}
                sourceDuration={library.duration_seconds}
                appearance="overlay"
                showExportHint={false}
                i18nPrefix="communityLibrary.originalVideoDownload"
                onProRequired={() => setProDownloadOpen(true)}
              />
            )}
            {library.youtube_video_id && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0 h-8 w-8 p-0"
                    asChild
                  >
                    <a
                      href={`https://www.youtube.com/watch?v=${library.youtube_video_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('communityLibrary.watchOnYouTube')}
                </TooltipContent>
              </Tooltip>
            )}
            {onRemoveFromCampaign && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0 h-8 w-8 p-0 text-destructive hover:text-destructive"
                    aria-label={t('campaign.removeVideo.trigger')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFromCampaign(library);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('campaign.removeVideo.trigger')}</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className={`flex items-start gap-1.5 text-[10px] leading-snug ${quotaExempt ? 'text-emerald-500' : 'text-muted-foreground'}`}>
            <CircleCheck aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0" />
            <span>{t(quotaExempt ? 'campaign.clipQuotaExemption' : 'campaign.clipQuotaStandardUsage')}</span>
          </div>
        </div>
      </Card>

      <ProFeatureDialog
        open={proDownloadOpen}
        onOpenChange={setProDownloadOpen}
        onUpgrade={() => navigate('/plans')}
        title={t('communityLibrary.originalVideoDownload.proTitle')}
        body={t('communityLibrary.originalVideoDownload.proBody')}
        dismissLabel={t('communityLibrary.originalVideoDownload.proDismiss')}
        upgradeLabel={t('communityLibrary.originalVideoDownload.proUpgrade')}
        testIdPrefix="campaign-original-download-pro"
      />
    </>
  );
};

export default CampaignVideoCard;
